import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminTableBody,
  AdminDetailTableHeader,
  AdminSupportingText,
  AdminCompactFormPage,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  adminFormTwoColGridClass,
  AdminFormDropdownChevron,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  ABN_FORMAT_OPTIONS,
  BOUNDARY_MODE_OPTIONS,
  COUNTRY_OPTIONS,
  DATE_ADJUST_UNITS,
  DATE_ADJUSTMENT_OPTIONS,
  DATE_COMPARE_WITH_OPTIONS,
  DATE_COMPARISON_OPTIONS,
  DATE_FORMAT_OPTIONS,
  DATETIME_ADJUST_UNITS,
  DATETIME_FORMAT_OPTIONS,
  DECIMAL_PRECISION_OPTIONS,
  DOMAIN_MODE_OPTIONS,
  DUPLICATE_SCOPE_OPTIONS,
  DUPLICATE_SOURCE_OPTIONS,
  EXTERNAL_SERVICE_OPTIONS,
  FILE_SIZE_SCOPE_OPTIONS,
  FILE_SIZE_UNIT_OPTIONS,
  FILE_TYPE_OPTIONS,
  FORMAT_CHECK_OPTIONS,
  PHONE_COUNTRY_MODE_OPTIONS,
  PHONE_INPUT_OPTIONS,
  RELATIONSHIP_OPTIONS,
  TEXT_MATCH_OPTIONS,
  TIME_FORMAT_OPTIONS,
  TIMEZONE_OPTIONS,
  UNAVAILABLE_MESSAGE,
  VAL_MODULE_OPTIONS,
  VERIFICATION_CODE_FORMAT_OPTIONS,
  VERIFICATION_SOURCE_OPTIONS,
  WEB_PROTOCOL_OPTIONS,
  YES_NO_OPTIONS,
  buildSampleValidationRules,
  createEmptyCondition,
  createEmptyValidationForm,
  emptyConditionSettings,
  formatBaseCheckSettings,
  formatConditionSettings,
  formatValidationDateTime,
  getBaseCheckLabel,
  getBaseChecksForField,
  getConditionTypeLabel,
  getConditionTypesForField,
  getEntryPointsForModule,
  getFieldDefinition,
  getInputFieldsForEntry,
  getTriggerLabel,
  getTriggersForEntry,
  getValidationModuleLabel,
  nextValidationRuleId,
  normalizeValidationForm,
} from '../../../data/validationConfig';
import {
  collectValidationSaveErrors,
  getEditRuleSource,
  getPublishedRule,
  mergeRulesAfterActivate,
} from '../../../data/validationRuleWorkflow';
import ConfigurationRuleTestResultPanel from './shared/ConfigurationRuleTestResultPanel';
import { matchesUrlName } from '../../../utils/slugify';

function emptyForm() {
  return {
    ...createEmptyValidationForm(),
    version: 1,
    pendingDraft: null,
    configurationHistory: [],
    testHistory: [],
  };
}

function mergeIncomingRule(raw, ruleIdParam) {
  const normalized = normalizeValidationForm(raw || {});
  return {
    ...emptyForm(),
    ...normalized,
    id: normalized.id ?? ruleIdParam ?? '',
    version: raw?.version ?? 1,
    pendingDraft: raw?.pendingDraft ?? null,
    configurationHistory: raw?.configurationHistory ?? [],
    testHistory: raw?.testHistory ?? [],
  };
}

const formatAuditDisplay = (value) => {
  if (!value || !String(value).trim()) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return value;
  }
};

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

const MultiChecklist = ({ label, options, values, onChange, disabled, error, required = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>
      {label} {required ? <span className="text-alertred">*</span> : null}
    </AdminDetailFieldLabel>
    <div className="space-y-2 border border-lightgray rounded-xl px-3 py-3 max-h-56 overflow-y-auto">
      {disabled || !options.length ? (
        <AdminSupportingText className="text-mediumgray">
          {disabled ? 'Complete the previous selections first.' : 'No compatible options available'}
        </AdminSupportingText>
      ) : (
        options.map((option) => (
          <label key={option.value} className="flex items-start gap-2 text-sm text-offblack">
            <input
              type="checkbox"
              className="accent-primaryblue mt-0.5 shrink-0"
              checked={(values || []).includes(option.value)}
              onChange={(e) => {
                const next = new Set(values || []);
                if (e.target.checked) next.add(option.value);
                else next.delete(option.value);
                onChange([...next]);
              }}
            />
            <span className="break-words">{option.label}</span>
          </label>
        ))
      )}
    </div>
    {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
  </div>
);

const withCurrentOption = (options, value, getLabel) => {
  if (!value || options.some((option) => option.value === value)) return options;
  return [...options, { value, label: `${getLabel(value)} (unavailable)` }];
};

const ValidationAddRule = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ruleName } = useParams();
  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;

  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleValidationRules(),
    [existingRulesFromState],
  );

  const publishedBaseline = useMemo(() => {
    if (isCreate) return null;
    if (ruleFromState?.id) {
      const fromList = allRules.find((r) => r.id === ruleFromState.id);
      return getPublishedRule(fromList || ruleFromState);
    }
    if (ruleName) {
      const found = allRules.find((r) => matchesUrlName(r.ruleName, ruleName));
      return getPublishedRule(found || null);
    }
    return null;
  }, [isCreate, ruleFromState, ruleName, allRules]);

  const resolvedSource = useMemo(() => {
    if (ruleFromState && isView) {
      const foundRecord = allRules.find((r) => r.id === ruleFromState.id) || ruleFromState;
      return mergeIncomingRule(getPublishedRule(foundRecord) || foundRecord, foundRecord.id);
    }
    if (ruleFromState) {
      const base = allRules.find((r) => r.id === ruleFromState.id) || ruleFromState;
      const source = isEdit ? getEditRuleSource(base) : getPublishedRule(base) || base;
      return mergeIncomingRule(source, base.id);
    }
    if (!isCreate && ruleName) {
      const found = allRules.find((r) => matchesUrlName(r.ruleName, ruleName));
      if (found) {
        const source = isEdit ? getEditRuleSource(found) : getPublishedRule(found);
        return mergeIncomingRule(source, found.id);
      }
    }
    return emptyForm();
  }, [ruleFromState, isCreate, isEdit, isView, ruleName, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [errors, setErrors] = useState({});
  const [testResultFeedback, setTestResultFeedback] = useState(null);

  useEffect(() => {
    setFormData(resolvedSource);
  }, [resolvedSource]);

  useEffect(() => {
    const activationError = location.state?.activationError;
    if (!activationError) return;
    setErrors((prev) => ({ ...prev, activation: activationError }));
    const { activationError: _removed, ...rest } = location.state || {};
    navigate(location.pathname, { replace: true, state: rest });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const testResult = location.state?.testResult;
    if (!testResult) return;
    setTestResultFeedback(testResult);
    const { testResult: _removed, ...rest } = location.state || {};
    navigate(location.pathname, { replace: true, state: rest });
  }, [location.state, location.pathname, navigate]);

  const scopeLocked = isEdit;
  const fieldDef = useMemo(
    () => getFieldDefinition(formData.entryPoint, formData.inputField),
    [formData.entryPoint, formData.inputField],
  );
  const entryOptions = useMemo(
    () => withCurrentOption(getEntryPointsForModule(formData.module), formData.entryPoint, (value) => value),
    [formData.module, formData.entryPoint],
  );
  const triggerOptions = useMemo(
    () => withCurrentOption(getTriggersForEntry(formData.entryPoint), formData.trigger, getTriggerLabel),
    [formData.entryPoint, formData.trigger],
  );
  const inputFieldOptions = useMemo(
    () => withCurrentOption(getInputFieldsForEntry(formData.entryPoint), formData.inputField, (value) => value),
    [formData.entryPoint, formData.inputField],
  );
  const baseCheckOptions = useMemo(
    () => withCurrentOption(getBaseChecksForField(formData.inputField), formData.baseCheck, getBaseCheckLabel),
    [formData.inputField, formData.baseCheck],
  );
  const conditionTypeOptions = useMemo(
    () => getConditionTypesForField(formData.inputField, formData.baseCheck),
    [formData.inputField, formData.baseCheck],
  );
  const relatedFieldOptions = useMemo(
    () => (fieldDef.relatedFields || []).map((label) => ({ value: label, label })),
    [fieldDef],
  );
  const fileTypeOptions = useMemo(
    () => FILE_TYPE_OPTIONS.filter((option) => (fieldDef.fileTypes || []).includes(option.value)),
    [fieldDef],
  );
  const allowedValueOptions = useMemo(
    () => (fieldDef.choices || []).map((label) => ({ value: label, label })),
    [fieldDef],
  );

  const clearErrorKeys = (keys) => {
    setErrors((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        delete next[key];
      });
      return next;
    });
  };

  const handleChange = (key, value) => {
    clearErrorKeys([key, 'duplicate', 'activation', 'conditions']);
    setFormData((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'module') {
        next.entryPoint = '';
        next.trigger = '';
        next.inputField = '';
        next.baseCheck = '';
        next.conditions = [];
      }
      if (key === 'entryPoint') {
        const allowedTriggers = getTriggersForEntry(value).map((option) => option.value);
        next.trigger = allowedTriggers.includes(prev.trigger) ? prev.trigger : '';
        const allowedFields = getInputFieldsForEntry(value).map((option) => option.value);
        if (!allowedFields.includes(prev.inputField)) next.inputField = '';
      }
      return next;
    });
  };

  const updateBaseSetting = (settingKey, value) => {
    clearErrorKeys(['baseCheckSettings', 'activation']);
    setFormData((prev) => ({
      ...prev,
      baseCheckSettings: { ...(prev.baseCheckSettings || {}), [settingKey]: value },
    }));
  };

  const updateCondition = (index, patch) => {
    clearErrorKeys([`condition_${index}`, `condition_${index}_message`, 'conditions', 'activation']);
    setFormData((prev) => {
      const conditions = [...(prev.conditions || [])];
      const current = { ...conditions[index], ...patch };
      if (patch.type) current.settings = { ...emptyConditionSettings(patch.type), ...(patch.settings || {}) };
      conditions[index] = current;
      return { ...prev, conditions };
    });
  };

  const updateConditionSetting = (index, settingKey, value) => {
    clearErrorKeys([`condition_${index}`, 'conditions', 'activation']);
    setFormData((prev) => {
      const conditions = [...(prev.conditions || [])];
      conditions[index] = {
        ...conditions[index],
        settings: { ...(conditions[index].settings || {}), [settingKey]: value },
      };
      return { ...prev, conditions };
    });
  };

  const validateFull = () => {
    const validation = collectValidationSaveErrors(formData, allRules, formData.id || undefined, {
      originalLastUpdated: resolvedSource.lastUpdated,
    });
    const next = { ...validation.errors };
    if (validation.summary.length) next.activation = validation.summary.join(' ');
    setErrors(next);
    return validation.summary.length === 0;
  };

  const buildRecord = (nowIso, userLabel) => {
    const id = formData.id || nextValidationRuleId(allRules);
    const isNew = !formData.id;
    return {
      ...formData,
      id,
      ruleName: formData.ruleName.trim(),
      changeReason: formData.changeReason.trim(),
      version: formData.version || publishedBaseline?.version || 1,
      pendingDraft: formData.pendingDraft ?? null,
      configurationHistory: [
        ...(formData.configurationHistory || []),
        {
          action: isNew ? 'Add' : 'Edit',
          field: 'Rule',
          previousValue: isNew ? '—' : publishedBaseline?.ruleName || '—',
          newValue: formData.ruleName.trim(),
          status: 'saved',
          actor: userLabel,
          timestamp: nowIso,
          changeReason: formData.changeReason.trim(),
        },
      ],
      testHistory: formData.testHistory || [],
      dateAdded: isNew ? nowIso : formData.dateAdded || nowIso,
      addedBy: isNew ? userLabel : formData.addedBy || userLabel,
      lastUpdated: nowIso,
      lastUpdatedBy: userLabel,
    };
  };

  const handleSave = () => {
    const nowIso = new Date().toISOString();
    const userLabel = 'Olivia Brown';
    setErrors((prev) => ({ ...prev, activation: undefined }));
    if (!validateFull()) return;
    const record = buildRecord(nowIso, userLabel);
    const merged = mergeRulesAfterActivate(allRules, record, publishedBaseline || {});
    navigate('/admin/configuration/validation', { state: { rules: merged } });
  };

  const renderSelect = (name, label, options, required, placeholder = 'Select...', disabled = false, onValue) => {
    if (isView) {
      const selected = options.find((option) => option.value === formData[name]);
      return <TextValue label={label} value={selected?.label || formData[name] || '—'} />;
    }
    const emptyCompatible = !disabled && !options.length;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required && <span className="text-alertred">*</span>}
        </AdminDetailFieldLabel>
        <div className="relative w-full">
          <select
            id={`validation-rule-select-${name}`}
            value={formData[name] || ''}
            title={options.find((option) => option.value === formData[name])?.label || placeholder}
            onChange={(e) => (onValue ? onValue(e.target.value) : handleChange(name, e.target.value))}
            disabled={disabled || emptyCompatible}
            className={adminFormDetailSelectClasses(disabled || emptyCompatible, Boolean(errors[name]))}
          >
            <option value="">{emptyCompatible ? 'No compatible options available' : placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <AdminFormDropdownChevron />
        </div>
        {emptyCompatible ? <AdminSupportingText className="text-mediumgray">No compatible options available</AdminSupportingText> : null}
        {errors[name] && <AdminFormFieldError>{errors[name]}</AdminFormFieldError>}
      </div>
    );
  };

  const renderInput = (name, label, placeholder, required = false) => {
    if (isView) return <TextValue label={label} value={formData[name] || '—'} />;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required && <span className="text-alertred">*</span>}
        </AdminDetailFieldLabel>
        <input
          type="text"
          placeholder={placeholder}
          value={formData[name] || ''}
          onChange={(e) => handleChange(name, e.target.value)}
          className={adminFormDetailInputClasses(false, Boolean(errors[name]))}
        />
        {errors[name] && <AdminFormFieldError>{errors[name]}</AdminFormFieldError>}
      </div>
    );
  };

  const renderTextarea = (name, label, placeholder, required = false) => {
    if (isView) return <TextValue label={label} value={formData[name]} />;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required ? <span className="text-alertred">*</span> : null}
        </AdminDetailFieldLabel>
        <textarea
          rows={4}
          value={formData[name] || ''}
          onChange={(e) => handleChange(name, e.target.value)}
          placeholder={placeholder}
          className={adminFormDetailTextareaClasses(false, Boolean(errors[name]))}
        />
        {errors[name] ? <AdminFormFieldError>{errors[name]}</AdminFormFieldError> : null}
      </div>
    );
  };

  const renderSettingSelect = (label, value, options, onChange, required = true, placeholder = 'Select...') => {
    if (isView) {
      const selected = options.find((option) => option.value === value);
      return <TextValue label={label} value={selected?.label || value || '—'} />;
    }
    const emptyCompatible = !options.length;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required ? <span className="text-alertred">*</span> : null}
        </AdminDetailFieldLabel>
        <div className="relative w-full">
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={emptyCompatible}
            className={adminFormDetailSelectClasses(emptyCompatible, Boolean(errors.baseCheckSettings))}
          >
            <option value="">{emptyCompatible ? 'No compatible options available' : placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <AdminFormDropdownChevron />
        </div>
        {emptyCompatible ? <AdminSupportingText className="text-mediumgray">No compatible options available</AdminSupportingText> : null}
      </div>
    );
  };

  const renderSettingInput = (label, value, onChange, placeholder, required = false, error = false) => {
    if (isView) return <TextValue label={label} value={value || '—'} />;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required ? <span className="text-alertred">*</span> : null}
        </AdminDetailFieldLabel>
        <input
          type="text"
          placeholder={placeholder}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={adminFormDetailInputClasses(false, error)}
        />
      </div>
    );
  };

  const settings = formData.baseCheckSettings || {};
  const check = formData.baseCheck;

  const baseCheckSettingsBlock = (
    <>
      {check === 'email_address' || check === 'web_address' ? (
        <>
          {check === 'web_address'
            ? renderSettingSelect('Web Protocol', settings.webProtocol, WEB_PROTOCOL_OPTIONS, (value) => updateBaseSetting('webProtocol', value))
            : null}
          {renderSettingSelect('Domain setting', settings.domainMode, DOMAIN_MODE_OPTIONS, (value) => updateBaseSetting('domainMode', value))}
          {settings.domainMode === 'selected_domains' ? (
            <>
              {isView ? (
                <TextValue label="Selected Domains" value={(settings.domains || []).join('\n')} />
              ) : (
                <div className="space-y-1 w-full min-w-0">
                  <AdminDetailFieldLabel>
                    Selected Domains <span className="text-alertred">*</span>
                  </AdminDetailFieldLabel>
                  <textarea
                    rows={3}
                    placeholder="Enter one domain per line"
                    value={(settings.domains || []).join('\n')}
                    onChange={(e) => updateBaseSetting('domains', e.target.value.split('\n'))}
                    className={adminFormDetailTextareaClasses(false, Boolean(errors.baseCheckSettings))}
                  />
                </div>
              )}
              {renderSettingSelect('Allow Subdomains', settings.allowSubdomains, YES_NO_OPTIONS, (value) => updateBaseSetting('allowSubdomains', value))}
            </>
          ) : null}
        </>
      ) : null}
      {check === 'phone_number' ? (
        <>
          {renderSettingSelect('Phone Input', settings.phoneInput, PHONE_INPUT_OPTIONS, (value) => updateBaseSetting('phoneInput', value))}
          {renderSettingSelect('Countries', settings.phoneCountryMode, PHONE_COUNTRY_MODE_OPTIONS, (value) => updateBaseSetting('phoneCountryMode', value))}
          {settings.phoneCountryMode === 'selected_countries' ? (
            isView ? (
              <TextValue label="Selected Countries" value={(settings.countries || []).map((value) => COUNTRY_OPTIONS.find((option) => option.value === value)?.label || value).join(', ')} />
            ) : (
              <MultiChecklist
                label="Selected Countries"
                options={COUNTRY_OPTIONS}
                values={settings.countries}
                onChange={(value) => updateBaseSetting('countries', value)}
                error={errors.baseCheckSettings}
                required
              />
            )
          ) : null}
          {settings.phoneInput === 'national' ? (
            renderSettingSelect('Country Field', settings.countryField, relatedFieldOptions, (value) => updateBaseSetting('countryField', value), false, 'Select country field')
          ) : null}
        </>
      ) : null}
      {check === 'abn' ? renderSettingSelect('ABN Format', settings.abnFormat, ABN_FORMAT_OPTIONS, (value) => updateBaseSetting('abnFormat', value)) : null}
      {check === 'decimal_number' ? (
        <>
          {renderSettingSelect('Decimal Precision', settings.decimalPrecision, DECIMAL_PRECISION_OPTIONS, (value) => updateBaseSetting('decimalPrecision', value))}
          {settings.decimalPrecision === 'maximum'
            ? renderSettingInput('Maximum Decimal Places', settings.maxDecimalPlaces, (value) => updateBaseSetting('maxDecimalPlaces', value.replace(/[^\d]/g, '')), 'Enter 0 or more', true)
            : null}
        </>
      ) : null}
      {check === 'date' ? renderSettingSelect('Date Format', settings.dateFormat, DATE_FORMAT_OPTIONS, (value) => updateBaseSetting('dateFormat', value)) : null}
      {check === 'time' ? renderSettingSelect('Time Format', settings.timeFormat, TIME_FORMAT_OPTIONS, (value) => updateBaseSetting('timeFormat', value)) : null}
      {check === 'date_and_time' ? renderSettingSelect('Date and Time Format', settings.dateTimeFormat, DATETIME_FORMAT_OPTIONS, (value) => updateBaseSetting('dateTimeFormat', value)) : null}
      {check === 'verification_code' ? renderSettingSelect('Verification Code Format', settings.verificationCodeFormat, VERIFICATION_CODE_FORMAT_OPTIONS, (value) => updateBaseSetting('verificationCodeFormat', value)) : null}
      {errors.baseCheckSettings ? <AdminFormFieldError>{errors.baseCheckSettings}</AdminFormFieldError> : null}
    </>
  );

  const renderConditionSettings = (condition, index) => {
    const conditionSettings = condition.settings || {};
    const type = condition.type;
    const error = Boolean(errors[`condition_${index}`]);
    if (!type || type === 'required_check') {
      return isView ? <TextValue label="Condition Settings" value="None" /> : <AdminSupportingText className="text-mediumgray">None</AdminSupportingText>;
    }
    if (type === 'format_check') {
      return renderSettingSelect('Format Option', conditionSettings.formatOption, FORMAT_CHECK_OPTIONS, (value) => updateConditionSetting(index, 'formatOption', value));
    }
    if (type === 'character_limit_check') {
      return (
        <>
          {renderSettingInput('Minimum characters', conditionSettings.minChars, (value) => updateConditionSetting(index, 'minChars', value.replace(/[^\d]/g, '')), 'Enter 0 or more', false, error)}
          {renderSettingInput('Maximum characters', conditionSettings.maxChars, (value) => updateConditionSetting(index, 'maxChars', value.replace(/[^\d]/g, '')), 'Enter 1 or more', false, error)}
        </>
      );
    }
    if (type === 'number_range_check') {
      return (
        <>
          {renderSettingInput('Minimum', conditionSettings.minNum, (value) => updateConditionSetting(index, 'minNum', value), 'Enter minimum', false, error)}
          {renderSettingSelect('Include Minimum', conditionSettings.minInclude, BOUNDARY_MODE_OPTIONS, (value) => updateConditionSetting(index, 'minInclude', value), false)}
          {renderSettingInput('Maximum', conditionSettings.maxNum, (value) => updateConditionSetting(index, 'maxNum', value), 'Enter maximum', false, error)}
          {renderSettingSelect('Include Maximum', conditionSettings.maxInclude, BOUNDARY_MODE_OPTIONS, (value) => updateConditionSetting(index, 'maxInclude', value), false)}
        </>
      );
    }
    if (type === 'file_type_check') {
      return isView ? (
        <TextValue label="File Types" value={formatConditionSettings(condition)} />
      ) : (
        <MultiChecklist
          label="File Types"
          options={fileTypeOptions}
          values={conditionSettings.fileTypes}
          onChange={(value) => updateConditionSetting(index, 'fileTypes', value)}
          error={errors[`condition_${index}`]}
          required
        />
      );
    }
    if (type === 'file_count_check') {
      return (
        <>
          {renderSettingInput('Minimum files', conditionSettings.minFiles, (value) => updateConditionSetting(index, 'minFiles', value.replace(/[^\d]/g, '')), 'Enter 0 or more', false, error)}
          {renderSettingInput('Maximum files', conditionSettings.maxFiles, (value) => updateConditionSetting(index, 'maxFiles', value.replace(/[^\d]/g, '')), 'Enter 1 or more', false, error)}
        </>
      );
    }
    if (type === 'file_size_check') {
      return (
        <>
          {renderSettingInput('Minimum size', conditionSettings.minSize, (value) => updateConditionSetting(index, 'minSize', value.replace(/[^\d.]/g, '')), 'Enter minimum', false, error)}
          {renderSettingInput('Maximum size', conditionSettings.maxSize, (value) => updateConditionSetting(index, 'maxSize', value.replace(/[^\d.]/g, '')), 'Enter maximum', false, error)}
          {renderSettingSelect('Size unit', conditionSettings.sizeUnit, FILE_SIZE_UNIT_OPTIONS, (value) => updateConditionSetting(index, 'sizeUnit', value))}
          {renderSettingSelect('Size scope', conditionSettings.sizeScope, FILE_SIZE_SCOPE_OPTIONS, (value) => updateConditionSetting(index, 'sizeScope', value))}
        </>
      );
    }
    if (type === 'image_dimensions_check') {
      return (
        <>
          {renderSettingInput('Minimum width', conditionSettings.minWidth, (value) => updateConditionSetting(index, 'minWidth', value.replace(/[^\d]/g, '')), 'Pixels', false, error)}
          {renderSettingInput('Maximum width', conditionSettings.maxWidth, (value) => updateConditionSetting(index, 'maxWidth', value.replace(/[^\d]/g, '')), 'Pixels', false, error)}
          {renderSettingInput('Minimum height', conditionSettings.minHeight, (value) => updateConditionSetting(index, 'minHeight', value.replace(/[^\d]/g, '')), 'Pixels', false, error)}
          {renderSettingInput('Maximum height', conditionSettings.maxHeight, (value) => updateConditionSetting(index, 'maxHeight', value.replace(/[^\d]/g, '')), 'Pixels', false, error)}
          {renderSettingInput('Allowed aspect ratios', conditionSettings.aspectRatios, (value) => updateConditionSetting(index, 'aspectRatios', value), 'e.g. 16:9, 4:3')}
        </>
      );
    }
    if (type === 'allowed_value_check') {
      if (allowedValueOptions.length) {
        return isView ? (
          <TextValue label="Allowed Values" value={(conditionSettings.allowedValues || []).join(', ')} />
        ) : (
          <MultiChecklist
            label="Allowed Values"
            options={allowedValueOptions}
            values={conditionSettings.allowedValues}
            onChange={(value) => updateConditionSetting(index, 'allowedValues', value)}
            error={errors[`condition_${index}`]}
            required
          />
        );
      }
      return isView ? (
        <TextValue label="Allowed Values" value={(conditionSettings.allowedValues || []).join('\n')} />
      ) : (
        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            Allowed Values <span className="text-alertred">*</span>
          </AdminDetailFieldLabel>
          <textarea
            rows={3}
            placeholder="Enter one value per line"
            value={(conditionSettings.allowedValues || []).join('\n')}
            onChange={(e) => updateConditionSetting(index, 'allowedValues', e.target.value.split('\n'))}
            className={adminFormDetailTextareaClasses(false, error)}
          />
        </div>
      );
    }
    if (type === 'date_check') {
      const adjustUnits = check === 'time' ? [] : check === 'date' ? DATE_ADJUST_UNITS : DATETIME_ADJUST_UNITS;
      return (
        <>
          {renderSettingSelect('Comparison', conditionSettings.comparison, DATE_COMPARISON_OPTIONS, (value) => updateConditionSetting(index, 'comparison', value))}
          {renderSettingSelect('Compare With', conditionSettings.compareWith, DATE_COMPARE_WITH_OPTIONS, (value) => updateConditionSetting(index, 'compareWith', value))}
          {conditionSettings.compareWith === 'fixed_date_time'
            ? renderSettingInput('Fixed Date/Time', conditionSettings.fixedDateTime, (value) => updateConditionSetting(index, 'fixedDateTime', value), 'Enter date/time', true, error)
            : null}
          {conditionSettings.compareWith === 'another_compatible_field'
            ? renderSettingSelect('Compare Field', conditionSettings.compareField, relatedFieldOptions, (value) => updateConditionSetting(index, 'compareField', value))
            : null}
          {renderSettingSelect('Adjust Comparison Value', conditionSettings.adjustment, DATE_ADJUSTMENT_OPTIONS, (value) => updateConditionSetting(index, 'adjustment', value), false)}
          {conditionSettings.adjustment && conditionSettings.adjustment !== 'no_adjustment' ? (
            <>
              {renderSettingInput('Amount', conditionSettings.adjustAmount, (value) => updateConditionSetting(index, 'adjustAmount', value.replace(/[^\d]/g, '')), 'Enter a positive number', true, error)}
              {renderSettingSelect('Unit', conditionSettings.adjustUnit, adjustUnits, (value) => updateConditionSetting(index, 'adjustUnit', value))}
            </>
          ) : null}
          {conditionSettings.compareWith === 'current_date_time' || conditionSettings.compareWith === 'another_compatible_field'
            ? renderSettingSelect('Time Zone', conditionSettings.timeZone, TIMEZONE_OPTIONS, (value) => updateConditionSetting(index, 'timeZone', value))
            : null}
        </>
      );
    }
    if (type === 'duplicate_check') {
      return (
        <>
          {renderSettingSelect('Source', conditionSettings.duplicateSource, DUPLICATE_SOURCE_OPTIONS, (value) => updateConditionSetting(index, 'duplicateSource', value))}
          {renderSettingSelect('Scope', conditionSettings.duplicateScope, DUPLICATE_SCOPE_OPTIONS, (value) => updateConditionSetting(index, 'duplicateScope', value))}
          {renderSettingSelect('Field', conditionSettings.duplicateField, relatedFieldOptions.length ? relatedFieldOptions : [{ value: formData.inputField, label: formData.inputField }], (value) => updateConditionSetting(index, 'duplicateField', value))}
          {renderSettingSelect('Match Option', conditionSettings.matchOption, TEXT_MATCH_OPTIONS, (value) => updateConditionSetting(index, 'matchOption', value), false)}
        </>
      );
    }
    if (type === 'relationship_check') {
      return (
        <>
          {renderSettingSelect('Relationship', conditionSettings.relationship, RELATIONSHIP_OPTIONS, (value) => updateConditionSetting(index, 'relationship', value))}
          {['matches_another_field', 'differs_from_another_field'].includes(conditionSettings.relationship)
            ? renderSettingSelect('Related Field', conditionSettings.relatedField, relatedFieldOptions, (value) => updateConditionSetting(index, 'relatedField', value))
            : null}
          {conditionSettings.relationship === 'exists_in_source'
            ? renderSettingSelect('Source', conditionSettings.relatedSource, DUPLICATE_SOURCE_OPTIONS, (value) => updateConditionSetting(index, 'relatedSource', value))
            : null}
          {['matches_another_field', 'differs_from_another_field'].includes(conditionSettings.relationship)
            ? renderSettingSelect('Match Option', conditionSettings.matchOption, TEXT_MATCH_OPTIONS, (value) => updateConditionSetting(index, 'matchOption', value), false)
            : null}
        </>
      );
    }
    if (type === 'verification_code_check') {
      return (
        <>
          {renderSettingSelect('Verification Source', conditionSettings.verificationSource, VERIFICATION_SOURCE_OPTIONS, (value) => updateConditionSetting(index, 'verificationSource', value))}
          {renderSettingSelect('Code Field', conditionSettings.verificationCodeField, relatedFieldOptions, (value) => updateConditionSetting(index, 'verificationCodeField', value), false, 'Select code field')}
        </>
      );
    }
    if (type === 'external_check') {
      const service = EXTERNAL_SERVICE_OPTIONS.find((option) => option.value === conditionSettings.externalService);
      const checkOptions = (service?.checks || []).map((value) => ({ value, label: value.replace(/_/g, ' ') }));
      const resultOptions = (service?.results || []).map((value) => ({ value, label: value.replace(/_/g, ' ') }));
      return (
        <>
          {renderSettingSelect('Service', conditionSettings.externalService, EXTERNAL_SERVICE_OPTIONS.filter((option) => option.enabled), (value) => {
            updateCondition(index, {
              settings: { ...emptyConditionSettings('external_check'), externalService: value },
            });
          })}
          {renderSettingSelect('Check', conditionSettings.externalCheck, checkOptions, (value) => updateConditionSetting(index, 'externalCheck', value))}
          {isView ? (
            <TextValue label="Accepted Results" value={(conditionSettings.acceptedResults || []).join(', ')} />
          ) : (
            <MultiChecklist
              label="Accepted Results"
              options={resultOptions}
              values={conditionSettings.acceptedResults}
              onChange={(value) => updateConditionSetting(index, 'acceptedResults', value)}
              disabled={!service}
              error={errors[`condition_${index}`]}
              required
            />
          )}
        </>
      );
    }
    return null;
  };

  const conditionBlock = isView ? (
    (formData.conditions || []).length ? (
      (formData.conditions || []).map((condition, index) => (
        <div key={condition.id || index} className="sm:col-span-2 space-y-4">
          <TextValue label="Condition Name" value={condition.name} />
          <TextValue label="Condition Type" value={getConditionTypeLabel(condition.type)} />
          <TextValue label="Condition Settings" value={formatConditionSettings(condition)} />
          <TextValue label="Validation Message" value={condition.message} />
        </div>
      ))
    ) : (
      <div className="sm:col-span-2">
        <TextValue label="Conditions" value="No Conditions" />
      </div>
    )
  ) : (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <AdminDetailFieldLabel className="!mb-0">Optional Conditions</AdminDetailFieldLabel>
        <AdminSupportingText className="mb-0">
          {(formData.conditions || []).length
            ? `${(formData.conditions || []).length} added`
            : 'No Conditions added'}
        </AdminSupportingText>
      </div>
      {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}
      {(formData.conditions || []).length ? (
        <div className="space-y-3">
          {(formData.conditions || []).map((condition, index) => {
            const typeOptions = withCurrentOption(conditionTypeOptions, condition.type, getConditionTypeLabel);
            return (
              <div key={condition.id || index} className="rounded-lg border border-lightgray p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <AdminSupportingText className="mb-0 font-semibold !text-offblack">
                    {condition.name || 'Condition'}
                  </AdminSupportingText>
                  <button
                    type="button"
                    onClick={() => handleChange('conditions', (formData.conditions || []).filter((_, conditionIndex) => conditionIndex !== index))}
                    className="shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred"
                    aria-label="Remove Condition"
                  >
                    <i className="fi fi-rs-trash text-sm" aria-hidden />
                  </button>
                </div>
                {errors[`condition_${index}`] ? <AdminFormFieldError>{errors[`condition_${index}`]}</AdminFormFieldError> : null}
                {renderSettingInput('Condition Name', condition.name, (value) => updateCondition(index, { name: value }), 'Enter condition name', true, Boolean(errors[`condition_${index}`]))}
                <div className="space-y-1 w-full min-w-0">
                  <AdminDetailFieldLabel>
                    Condition Type <span className="text-alertred">*</span>
                  </AdminDetailFieldLabel>
                  <div className="relative w-full">
                    <select
                      value={condition.type || ''}
                      onChange={(e) => updateCondition(index, { type: e.target.value })}
                      disabled={!typeOptions.length}
                      className={adminFormDetailSelectClasses(!typeOptions.length, Boolean(errors[`condition_${index}`]))}
                    >
                      <option value="">{typeOptions.length ? 'Select condition type' : 'No compatible options available'}</option>
                      {typeOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <AdminFormDropdownChevron />
                  </div>
                </div>
                {renderConditionSettings(condition, index)}
                <div className="space-y-1 w-full min-w-0">
                  <AdminDetailFieldLabel>
                    Validation Message <span className="text-alertred">*</span>
                  </AdminDetailFieldLabel>
                  <textarea
                    rows={3}
                    value={condition.message || ''}
                    onChange={(e) => updateCondition(index, { message: e.target.value })}
                    placeholder="Enter the message shown when this condition fails"
                    className={adminFormDetailTextareaClasses(false, Boolean(errors[`condition_${index}_message`]))}
                  />
                  {errors[`condition_${index}_message`] ? <AdminFormFieldError>{errors[`condition_${index}_message`]}</AdminFormFieldError> : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => handleChange('conditions', [...(formData.conditions || []), createEmptyCondition()])}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-primaryblue hover:text-brightblue"
      >
        <i className="fi fi-rs-plus text-xs" aria-hidden />
        Add Condition
      </button>
    </div>
  );

  const showRuleId = Boolean(formData.id);
  const configurationHistory = formData.configurationHistory || [];
  const testHistory = formData.testHistory || [];

  const configurationHistoryTable = configurationHistory.length > 0 ? (
    <div className="space-y-4">
      <AdminPanelTitle className="text-offblack !mb-0">Configuration History</AdminPanelTitle>
      <div className="overflow-x-auto min-w-0">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="border-b border-lightgray">
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Action</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Field</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Previous</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>New</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Status</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Actor</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Timestamp</AdminDetailTableHeader></th>
            </tr>
          </thead>
          <tbody>
            {[...configurationHistory].reverse().map((row, index) => (
              <tr key={`${row.timestamp}-${index}`} className="border-b border-lightgray last:border-b-0">
                <td className="py-3 px-2"><AdminTableBody>{row.action}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{row.field}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{row.previousValue}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{row.newValue}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{row.status}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{row.actor}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{formatAuditDisplay(row.timestamp)}</AdminTableBody></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  ) : null;

  const testHistoryTable = testHistory.length > 0 ? (
    <div className="space-y-4">
      <AdminPanelTitle className="text-offblack !mb-0">Test History</AdminPanelTitle>
      <div className="overflow-x-auto min-w-0">
        <table className="w-full min-w-[560px]">
          <thead>
            <tr className="border-b border-lightgray">
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Result</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Summary</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Actor</AdminDetailTableHeader></th>
              <th className="text-left py-3 px-2"><AdminDetailTableHeader>Timestamp</AdminDetailTableHeader></th>
            </tr>
          </thead>
          <tbody>
            {[...testHistory].reverse().map((row, index) => (
              <tr key={`${row.timestamp}-${index}`} className="border-b border-lightgray last:border-b-0">
                <td className="py-3 px-2"><AdminTableBody>{row.result}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{row.summary}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{row.actor}</AdminTableBody></td>
                <td className="py-3 px-2"><AdminTableBody>{formatAuditDisplay(row.timestamp)}</AdminTableBody></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  ) : null;

  if (isView) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Validation Rule Detail</AdminPageTitle>
        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Rule Setup</AdminPanelTitle>
              {showRuleId ? (
                <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView />
              ) : null}
              <div className={adminFormTwoColGridClass}>
                <TextValue label="Rule Name" value={formData.ruleName} />
                <TextValue label="Module" value={getValidationModuleLabel(formData.module)} />
                <TextValue label="Entry Point" value={formData.entryPoint} />
                <TextValue label="Trigger" value={getTriggerLabel(formData.trigger)} />
                <TextValue label="Input Field" value={formData.inputField} />
                <TextValue label="Input Field Reference" value={fieldDef.reference} />
              </div>
            </div>
          </DashboardMainContainer>

          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Check Logic</AdminPanelTitle>
              <div className={adminFormTwoColGridClass}>
                <TextValue label="Base Check" value={getBaseCheckLabel(formData.baseCheck)} />
                <TextValue label="Base Check Settings" value={formatBaseCheckSettings(formData)} />
                <TextValue label="Required references" value={fieldDef.available ? 'Available' : 'Unavailable'} />
                {conditionBlock}
              </div>
            </div>
          </DashboardMainContainer>

          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="space-y-4">
              <AdminPanelTitle className="text-offblack mb-4">Rule Result</AdminPanelTitle>
              <div className={adminFormTwoColGridClass}>
                <div className="sm:col-span-2">
                  <TextValue label="Base Check Message" value={formData.baseCheckMessage} />
                </div>
                {(formData.conditions || []).length ? (
                  (formData.conditions || []).map((condition, index) => (
                    <div key={`msg-${condition.id || index}`} className="sm:col-span-2">
                      <TextValue label={`${condition.name || getConditionTypeLabel(condition.type)} Message`} value={condition.message} />
                    </div>
                  ))
                ) : (
                  <div className="sm:col-span-2">
                    <TextValue label="Condition Messages" value="No Condition Messages required" />
                  </div>
                )}
                <div className="sm:col-span-2">
                  <TextValue label="Unavailable message" value={UNAVAILABLE_MESSAGE} />
                </div>
                <TextValue label="Date Added" value={formatValidationDateTime(formData.dateAdded)} />
                <TextValue label="Added By" value={formData.addedBy} />
                <TextValue label="Last Updated" value={formatValidationDateTime(formData.lastUpdated)} />
                <TextValue label="Last Updated By" value={formData.lastUpdatedBy} />
              </div>
            </div>
          </DashboardMainContainer>

          {configurationHistoryTable ? (
            <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              {configurationHistoryTable}
            </DashboardMainContainer>
          ) : null}

          {testHistoryTable ? (
            <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              {testHistoryTable}
            </DashboardMainContainer>
          ) : null}
        </div>
        <ConfigurationRuleTestResultPanel testResult={testResultFeedback} />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={isCreate ? 'Validation Rule' : 'Edit Validation Rule'}
        onSubmit={handleSave}
        afterForm={<ConfigurationRuleTestResultPanel testResult={testResultFeedback} />}
      >
        <div className="space-y-4">
          <AdminPanelTitle className="text-offblack !mb-0">Rule Details</AdminPanelTitle>
          {!isCreate ? (
            <AdminConfigLockedField
              label="Rule ID"
              value={formData.id}
              mono
              isView={false}
            />
          ) : null}
          {renderInput('ruleName', 'Rule Name', 'Enter rule name', true)}
        </div>

        <div className="space-y-4">
          <AdminPanelTitle className="text-offblack !mb-0">Rule Scope</AdminPanelTitle>
        {scopeLocked ? (
          <>
            <AdminConfigLockedField label="Module" value={getValidationModuleLabel(formData.module)} isView={false} />
            <AdminConfigLockedField label="Entry Point" value={formData.entryPoint} isView={false} />
            <AdminConfigLockedField label="Trigger" value={getTriggerLabel(formData.trigger)} isView={false} />
            <AdminConfigLockedField label="Input Field" value={formData.inputField} isView={false} />
          </>
        ) : (
          <>
            {renderSelect('module', 'Module', VAL_MODULE_OPTIONS, true, 'Select module')}
            {renderSelect('entryPoint', 'Entry Point', entryOptions, true, formData.module ? 'Select entry point' : 'Select module first', !formData.module)}
            {renderSelect('trigger', 'Trigger', triggerOptions, true, formData.entryPoint ? 'Select trigger' : 'Select entry point first', !formData.entryPoint)}
            {renderSelect('inputField', 'Input Field', inputFieldOptions, true, formData.entryPoint ? 'Select input field' : 'Select entry point first', !formData.entryPoint)}
          </>
        )}
        {renderSelect('baseCheck', 'Base Check', baseCheckOptions, true, formData.inputField ? 'Select base check' : 'Select input field first', !formData.inputField)}
        {formData.baseCheck ? baseCheckSettingsBlock : null}
        </div>

        {conditionBlock}

        <div className="space-y-4">
          <AdminPanelTitle className="text-offblack !mb-0">Validation Messages</AdminPanelTitle>
        {renderTextarea('baseCheckMessage', 'Base Check Message', 'Enter the message shown when the base check fails', true)}
        {!(formData.conditions || []).length ? (
          <AdminSupportingText className="text-mediumgray">No Condition Messages required</AdminSupportingText>
        ) : null}
        <AdminConfigLockedField label="Unavailable message" value={UNAVAILABLE_MESSAGE} isView={false} />
        </div>
        {renderTextarea('changeReason', 'Change Reason', 'Enter a change reason', true)}
        {errors.duplicate ? <AdminFormFieldError>{errors.duplicate}</AdminFormFieldError> : null}
        {errors.activation ? <AdminFormFieldError>{errors.activation}</AdminFormFieldError> : null}
        {errors.concurrent ? <AdminFormFieldError>{errors.concurrent}</AdminFormFieldError> : null}
        {configurationHistoryTable}
        {testHistoryTable}
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default ValidationAddRule;
